'use client';

import * as React from 'react';
import { CheckIcon, ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
	Command,
	// CommandEmpty,
	CommandGroup,
	// CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from '@/components/ui/command';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import clsx from 'clsx';
import { Icon, Typography, TypographyType } from '../common';
import Icons from '@/const/icons';
import { Options } from './combobox';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

export type OptionsMultiple = {
	title: string;
	options: Options[];
};

interface IProps {
	disabled?: boolean;
	open: boolean;
	value: string | undefined;
	placeholder: string;
	options: OptionsMultiple[];
	title?: string;
	isDropdown?: boolean;
	buttonContent?: string | JSX.Element;
	classNameTrigger?: string;
	classNameButton?: string;
	classNamePopoverContent?: string;
	classNameCommand?: string;
	classNameCommandList?: string;
	typographyTypeLabel?: TypographyType;
	classNameCommandGroup?: string;
	classNameArrow?: string;
	containerRef?: (node: HTMLDivElement | null) => void;
	clearAll?: () => void;
	setOpen: (value: boolean) => void;
	setValue: (key: string, value?: string) => void;
}

export const ComboboxMultiple = ({
	placeholder,
	disabled,
	options,
	title,
	open,
	setOpen,
	value,
	setValue,
	clearAll,
	containerRef,
	typographyTypeLabel,
	classNameArrow,
	buttonContent,
	classNameTrigger,
	classNameButton,
	classNamePopoverContent,
	classNameCommand,
	isDropdown = true,
	classNameCommandList,
	classNameCommandGroup,
}: IProps) => {
	const [selected, setSelected] = React.useState<Options | undefined>();
	const getLabel = () => {
		let label = '';
		options.forEach((option) => {
			const findItem = option.options.find(
				(item) => item?.value?.toLowerCase() === value
			);
			if (findItem) {
				label = findItem.label as string;
			}
		});
		return label;
	};
	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger
				disabled={disabled}
				asChild={!buttonContent} // when buttonContent is not provided, we use the trigger as the button
				className={`p-2 !text-b14 !text-black border-smoke-50 rounded-lg justify-between md:justify-start w-full disabled:cursor-not-allowed ${classNameTrigger}`}
			>
				{buttonContent ? (
					buttonContent
				) : (
					<Button
						variant="outline"
						aria-expanded={open}
						className={clsx(
							'relative gap-2 min-w-max bg-white',
							classNameButton
						)}
					>
						<Typography
							type="semibold-14"
							className={clsx(
								'!line-clamp-1 text-ellipsis overflow-hidden whitespace-normal text-left opacity-60'
							)}
						>
							{value ? getLabel() : placeholder}
						</Typography>
						<ChevronDown
							className={`mt-0.5 h-4 w-5 shrink-0 text-[#0D0616] ${
								open && 'rotate-180'
							} ${classNameArrow}`}
						/>
						{value && (
							<span className="w-[10px] h-[10px] bg-main-blue rounded-full absolute -top-1 -right-1 z-10" />
						)}
					</Button>
				)}
			</PopoverTrigger>
			<PopoverContent
				className={clsx(
					'w-full md:w-[212px] p-0 border-none z-10 max-h-96 overflow-y-auto scroll-custom',
					classNamePopoverContent
				)}
				align="start"
			>
				{title && (
					<Typography type="semibold-16" className="mb-2 px-4 pt-3">
						{title}
					</Typography>
				)}
				{clearAll && (
					<OptionContainer
						setOpen={setOpen}
						setValue={(value) => setValue('clear', value)}
						value={value}
						classNameCommand={classNameCommand}
						clearAll={clearAll}
						typographyTypeLabel={typographyTypeLabel}
						setSelected={setSelected}
						selected={selected}
						classNameCommandGroup={classNameCommandGroup}
					/>
				)}
				{options.map((item, index) => (
					<React.Fragment key={'options-multilist-' + index}>
						<OptionContainer
							title={item.title}
							options={item.options}
							setOpen={setOpen}
							setValue={(value) => setValue(item.title, value)}
							value={value}
							classNameCommand={classNameCommand}
							classNameCommandList={classNameCommandList}
							classNameCommandGroup={classNameCommandGroup}
							typographyTypeLabel={typographyTypeLabel}
							containerRef={containerRef}
							isDropdown={isDropdown}
							setSelected={setSelected}
							selected={selected}
						/>
					</React.Fragment>
				))}
			</PopoverContent>
		</Popover>
	);
};

interface OptionContainerProsp {
	options?: Options[];
	title?: string;
	buttonContent?: string | JSX.Element;
	classNameCommandList?: string;
	classNameCommandGroup?: string;
	classNameCommand?: string;
	typographyTypeLabel?: TypographyType;
	clearAll?: () => void;
	setOpen: (value: boolean) => void;
	value?: string;
	isDropdown?: boolean;
	setValue: (value: string) => void;
	containerRef?: (node: HTMLDivElement | null) => void;
	setSelected: React.Dispatch<React.SetStateAction<Options | undefined>>;
	selected: Options | undefined;
}

export const OptionContainer: React.FC<OptionContainerProsp> = ({
	options,
	title,
	clearAll,
	setOpen,
	value,
	setValue,
	classNameCommand,
	classNameCommandList,
	classNameCommandGroup,
	typographyTypeLabel,
	isDropdown = true,
	containerRef,
	buttonContent,
	selected,
	setSelected,
}) => {
	const [openDisclosure, setOpenDisclosure] = React.useState<boolean>(false);
	return (
		<Command
			className={clsx('bg-white text-black !rounded-none', classNameCommand)}
		>
			<Disclosure defaultOpen>
				{title && !buttonContent && (
					<Disclosure.Button
						onClick={(e) => {
							if (!isDropdown) {
								return e.preventDefault();
							}
							setOpenDisclosure(!openDisclosure);
						}}
					>
						<div className="flex items-center justify-between mb-2 mt-3 px-4">
							<Typography type="semibold-14">{title}</Typography>
							{isDropdown && (
								<ChevronDownIcon
									className={`${
										openDisclosure ? '' : 'rotate-180 transform'
									} h-4 w-4 text-[#292D32]`}
								/>
							)}
						</div>
					</Disclosure.Button>
				)}
				<Disclosure.Panel>
					{/* THIS IS A INPUT SEARCH CAN BE IMPLEMENTED IF ITS NECESSARY */}
					{/* <CommandInput className="!text-black" placeholder="Search framework..." /> */}
					<CommandList className={clsx('!max-h-[192px]', classNameCommandList)}>
						{/* <CommandEmpty>No items found.</CommandEmpty> */}
						<CommandGroup className={clsx('!p-0', classNameCommandGroup)}>
							{clearAll && (
								<Button
									variant={'ghost'}
									className="font-normal flex justify-start items-center gap-2 h-12 px-4 w-full hover:bg-grey-100 !rounded-none text-sm"
									onClick={() => {
										clearAll();
										setOpen(false);
										setSelected(undefined);
									}}
								>
									<Icon src={Icons.broom} className="!w-5 !h-5" />
									Clear All
								</Button>
							)}
							{options?.map((option, index) => {
								const isLastElement = options?.length === index + 1;
								return (
									<CommandItem
										key={`multilist-${option.value}-${index}`}
										ref={isLastElement ? containerRef : null}
										value={option.value}
										className={clsx(
											'w-full bg-white py-3 flex items-center gap-2 cursor-pointer hover:bg-grey-100 !px-4',
											{ 'bg-grey-100': selected?.value === option.value }
										)}
										onSelect={(currentValue) => {
											setValue(currentValue === value ? '' : currentValue);
											setOpen(false);
											setSelected(option);
										}}
									>
										<div>{option.icon && option.icon}</div>
										<Typography
											type={typographyTypeLabel || 'regular-14'}
											className="line-clamp-1 text-ellipsis"
										>
											{option.label}
										</Typography>
										{selected?.value === option.value ? (
											<span className="absolute right-0 pr-4">
												<CheckIcon
													className="w-5 h-5 text-black"
													aria-hidden="true"
												/>
											</span>
										) : null}
									</CommandItem>
								);
							})}
						</CommandGroup>
					</CommandList>
				</Disclosure.Panel>
				<CommandSeparator className="mx-3 mt-2" />
			</Disclosure>
		</Command>
	);
};
